import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon
} from '@mui/icons-material'
import { useGetAdminDashboardQuery, useGetSystemHealthQuery, useGetCatalogStatsQuery } from '@store/api/adminApi'
import { formatAverageRatingDisplay } from '@utils/statDisplay'
import { formatDurationSeconds } from '@utils/durationDisplay'
import { formatAuditLogSummary } from '@utils/auditDisplay'
import { useAppSelector } from '@store/hooks'
import AdminPageGate from '@components/auth/AdminPageGate'
import FeatureUseSummaryPanel from '@components/admin/FeatureUseSummaryPanel'
import { Link as RouterLink } from 'react-router-dom'

const AdminDashboard: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useGetAdminDashboardQuery()
  const { isLoading: healthLoading } = useGetSystemHealthQuery()
  const { data: catalogStats } = useGetCatalogStatsQuery()

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Alert severity="error">
          Please log in to access the admin dashboard.
        </Alert>
      </Box>
    )
  }

  return (
    <AdminPageGate permission="analytics:read" title="Admin Dashboard">
      <AdminDashboardContent
        dashboardData={dashboardData}
        dashboardLoading={dashboardLoading}
        dashboardError={dashboardError}
        healthLoading={healthLoading}
        catalogStats={catalogStats}
      />
    </AdminPageGate>
  )
}

const AdminDashboardContent: React.FC<{
  dashboardData: ReturnType<typeof useGetAdminDashboardQuery>['data']
  dashboardLoading: boolean
  dashboardError: unknown
  healthLoading: boolean
  catalogStats: ReturnType<typeof useGetCatalogStatsQuery>['data']
}> = ({ dashboardData, dashboardLoading, dashboardError, healthLoading, catalogStats }) => {
  const metrics = dashboardData?.metrics
  const systemHealth = dashboardData?.systemHealth
  const recentUsers = dashboardData?.recentUsers || []
  const auditLogs = dashboardData?.auditLogs || []

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success'
      case 'warning': return 'warning'
      case 'critical': return 'error'
      default: return 'default'
    }
  }

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />
      case 'warning': return <WarningIcon />
      case 'critical': return <ErrorIcon />
      default: return <WarningIcon />
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Tooltip title="Refresh Data">
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <FeatureUseSummaryPanel />

      {(dashboardLoading || healthLoading) && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}

      {!dashboardLoading && !healthLoading && dashboardError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load dashboard data. Please try again.
        </Alert>
      ) : null}

      {!dashboardLoading && !healthLoading && !dashboardError ? (
      <>
      {/* System Health Alert */}
      {systemHealth && systemHealth.status !== 'healthy' && (
        <Alert 
          severity={systemHealth.status === 'critical' ? 'error' : 'warning'} 
          sx={{ mb: 3 }}
          icon={getHealthIcon(systemHealth.status)}
        >
          <Typography variant="h6" gutterBottom>
            System Health: {systemHealth.status.toUpperCase()}
          </Typography>
          {systemHealth.issues.length > 0 && (
            <List dense>
              {systemHealth.issues.map((issue: any, index: number) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={issue} />
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.activeUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.newUsersToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <StorageIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.totalCigars || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cigars
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {catalogStats && (
        <Box mb={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Catalog coverage</Typography>
            <Chip
              component={RouterLink}
              to="/admin/catalog-health"
              clickable
              label="Open Catalog Health"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h5">{catalogStats.summary.pctSized.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">Lines sized</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h5">{catalogStats.summary.emptyLines}</Typography>
                  <Typography variant="body2" color="text.secondary">Empty-vitola lines</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h5">{catalogStats.leaf.completeLeafPct.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">Full leaf coverage</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h5">
                    {catalogStats.summary.brands} / {catalogStats.summary.lines}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Brands / lines</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Additional Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <TimelineIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.totalSessions || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Smoking Sessions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <SpeedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {formatAverageRatingDisplay(metrics?.averageRating)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Rating
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {metrics?.databaseConnections || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    DB Connections
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <SecurityIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {formatDurationSeconds(metrics?.uptime || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uptime
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Performance */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Performance
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Memory Usage</Typography>
                    <Typography variant="body2">{metrics?.memoryUsage?.toFixed(1)} MB</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((metrics?.memoryUsage || 0) / 1000 * 100, 100)} 
                    color={metrics?.memoryUsage && metrics.memoryUsage > 500 ? 'error' : 'primary'}
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Response Time</Typography>
                    <Typography variant="body2">{metrics?.averageResponseTime?.toFixed(0)}ms</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((metrics?.averageResponseTime || 0) / 1000 * 100, 100)} 
                    color={metrics?.averageResponseTime && metrics.averageResponseTime > 500 ? 'error' : 'success'}
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Error Rate</Typography>
                    <Typography variant="body2">{(metrics?.errorRate || 0).toFixed(2)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics?.errorRate || 0} 
                    color={metrics?.errorRate && metrics.errorRate > 5 ? 'error' : 'success'}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Health
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {getHealthIcon(systemHealth?.status || 'unknown')}
                <Chip 
                  label={systemHealth?.status?.toUpperCase() || 'UNKNOWN'} 
                  color={getHealthColor(systemHealth?.status || 'unknown') as any}
                  variant="filled"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                System is running normally with {metrics?.databaseConnections || 0} active database connections.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date().toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Users
              </Typography>
              {recentUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent users
                </Typography>
              ) : (
                <List dense>
                  {recentUsers.slice(0, 5).map((user: any) => (
                    <ListItem key={user.id} divider>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.firstName?.[0] || user.email[0].toUpperCase()}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                            <Chip 
                              label={user.role} 
                              size="small" 
                              color={user.role === 'super_admin' ? 'error' : user.role === 'admin' ? 'warning' : 'default'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Audit Logs
              </Typography>
              {auditLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent audit logs
                </Typography>
              ) : (
                <List dense>
                  {auditLogs.slice(0, 5).map((log: any) => (
                    <ListItem key={log.id} divider>
                      <ListItemIcon>
                        <AdminIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatAuditLogSummary(log.action, log.resource, log.resourceId)}
                        secondary={new Date(log.timestamp).toLocaleString()}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      ) : null}
    </Box>
  )
}

export default AdminDashboard
